import React, { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { pick, uniqBy, omitBy, isNil, isEmpty, omit } from "lodash";
import useSession from "Hooks/session.hook";
import { useHistory } from "react-router-dom";
import { Button } from "@life-line-lab/components-react";
import { Radio, Spin, Tooltip } from "antd";
import { toaster, toNum } from "Helpers/index";
import { SortingMode } from "../../../types/common.types";
import {
  listTrolley,
  listBatches,
  listBoxes,
  createBatch,
  addToBatch,
  boxAssignDriver,
  updateBatchHandoverPoint,
} from "Services/delivery.services";
import {
  bulkLooseAssignDriver,
  updateOrdersHandoverPoint,
} from "Services/order.service";
import { fetchOrderList } from "Services/order.service";
import { useQuery } from "Hooks/index";
import SortingParcelsList from "./sorting-tabs/sorting-parcels-list";
import SortingBulkList from "./sorting-tabs/sorting-bulk-list";
import SortingLooseList from "./sorting-tabs/sorting-loose-list";
import SortingBatchesList from "./sorting-tabs/sorting-batches-list";
import SortingBoxesList from "./sorting-tabs/sorting-boxes-list";
import SortingTrolleyList from "./sorting-tabs/sorting-trolley-list";
import ErrorBoundary from "antd/lib/alert/ErrorBoundary";
import { LoadingOutlined, ReloadOutlined } from "@ant-design/icons";
import { useRecoilValue } from "recoil";
import { GlobalFilterAtom } from "Recoil/global_filter.atom";
import useCorporateType from "Hooks/corporate-type.hook";

interface SortingListProps {
  vehicleOptions?: any;
  customerOptions: any;
  sortingHubOptions: any;
  loadingMechanism: any;
  queryParams: any;
  itemPerPage: any;
  setCurrentListCount: any;
}

const useSortingList = (props) => {
  const {
    radioButtonState,
    queryParams,
    itemPerPage,
    setCurrentListCount,
    refreshPayload,
  } = props || {};
  const { t } = useTranslation();

  const [allOrderList, setAllOrderList] = useState<any>([]);
  const [allBatchesList, setAllBatchesList] = useState<any>([]);
  const [isChildLoading, setIsChildLoading] = useState<boolean>(false);
  const [driverOptions, setDriverOptions] = useState<any>([]);
  const statusForLooseBulk = useMemo(() => {
    return [
      "DROPPED_OFF",
      "IN_TROLLEY",
      "BOARDED",
      "RECEIVED",
      "UNLOADED",
      "DISPATCHED",
      "DELIVERY_IN_PROGRESS",
      "DELIVERY_NOT_POSSIBLE",
      "DELIVERY_REFUSED",
      "RETURNED",
      "NO_SHOW",
      "CLOSED",
      "HANDED_OVER",
    ];
  }, [allOrderList]);

  const handleSetUpDriverOptions = (data) => {
    let localDriverOptions: any = [];

    // loop is faster than map + compact
    for (let i = 0; i < data.length; i++) {
      const boxData = data[i];

      if (boxData?.assignedDriverData) {
        localDriverOptions.push({
          label: boxData.assignedDriverData.fullName,
          value: boxData.assignedDriver,
        });
      }
    }

    return localDriverOptions;
  };

  const handleAPICalls = (payload = {}) => {
    switch (radioButtonState) {
      case "0":
        handleListingParcelOrders(payload);
        handleListingBatches(payload, true);
        break;
      case "1":
        //Bulk
        handleListingBulkParcels(payload);
        break;
      case "2":
        //Loose
        handleListingLooseParcels(payload);
        break;
      case "3":
        //Batches
        handleListingBatches(payload, false);
        break;
      case "4":
        //Boxes
        handleListingBoxes(payload);
        break;
      case "5":
        //Trolley
        handleListingTrolley(payload);
        break;
      default:
        handleListingParcelOrders(payload);
        break;
    }
  };

  const handleListingParcelOrders = async (payload: any) => {
    //TODO: edit the offset filters once it moves to page
    setIsChildLoading && setIsChildLoading(true);

    let filters: any = omitBy(
      {
        ...payload,
        orderStatus: ["DROPPED_OFF"],
        orderType: "INDIVIDUAL",
        looseOrders: false,
      },
      isNil,
    );
    filters = payload.orgID ? omit(filters, ["ancestorOrgIDs"]) : filters;
    const responseData: any = await fetchOrderList(
      filters,
      {
        offset: queryParams.offset,
        limit: toNum(itemPerPage),
      },
      { sortBy: "orderStatus", mode: "DESC" },
    );

    if (responseData && !responseData.error) {
      setCurrentListCount(responseData.length);
      setAllOrderList(responseData);
    } else {
      setAllOrderList([]);
      toaster("error", t("error.badRequest"));
    }

    setIsChildLoading && setIsChildLoading(false);
  };

  const handleListingBulkParcels = async (payload: any) => {
    setIsChildLoading && setIsChildLoading(true);

    //TODO: edit the offset filters once it moves to page, change status to the appropriate one as well
    let filters: any = omitBy(
      { ...payload, orderStatus: statusForLooseBulk, orderType: "BULK" },
      isNil,
    );
    filters = payload.orgID ? omit(filters, ["ancestorOrgIDs"]) : filters;

    const responseData: any = await fetchOrderList(
      filters,
      {
        offset: queryParams.offset,
        limit: toNum(itemPerPage),
      },
      { sortBy: "updatedAt", mode: "DESC" },
    );

    if (responseData && !responseData.error) {
      setCurrentListCount(responseData.length);
      setAllOrderList(responseData);
    } else {
      toaster("error", t("error.badRequest"));
    }

    setIsChildLoading && setIsChildLoading(false);
  };

  const handleListingLooseParcels = async (payload: any) => {
    setIsChildLoading && setIsChildLoading(true);

    //TODO: edit the offset filters once it moves to page, change status to the appropriate one as well
    let filters: any = omitBy(
      { ...payload, orderStatus: statusForLooseBulk, orderType: "LOOSE" },
      isNil,
    );
    filters = payload.customerOrgIDs
      ? omit(filters, ["ancestorOrgIDs"])
      : filters;

    const responseData: any = await fetchOrderList(
      filters,
      {
        offset: queryParams.offset,
        limit: toNum(itemPerPage),
      },
      { sortBy: "orderStatus", mode: "DESC" },
    );

    if (responseData && !responseData.error) {
      setCurrentListCount(responseData.length);
      setAllOrderList(responseData);
    } else {
      toaster("error", t("error.badRequest"));
    }

    setIsChildLoading && setIsChildLoading(false);
  };

  // boolean value to detect the listing for batches for the modal in tab 1 or
  // the batches for main listing in tab 3.
  const handleListingBatches = async (payload: any, isModalBatches = false) => {
    const getFilters = (payloadToFilter: any) => {
      const baseFilters = omitBy(payloadToFilter, isNil);

      let filters: any = pick(baseFilters, [
        "customerOrgIDs",
        "sortingHubID",
        "orFields",
        "ancestorOrgIDs",
      ]);

      if (filters.customerOrgIDs) filters = omit(filters, ["ancestorOrgIDs"]);

      return filters;
    };

    setIsChildLoading && setIsChildLoading(true);

    let filters = getFilters(payload);

    const responseData = await listBatches(
      omitBy(filters, isNil),
      {
        offset: queryParams.offset,
        limit: toNum(itemPerPage),
      },
      { sortBy: "status", mode: SortingMode.DESC },
    );

    if (responseData && !responseData.error) {
      if (!isModalBatches) {
        setCurrentListCount(responseData.length);
        setAllOrderList(responseData);
      } else {
        setAllBatchesList(responseData);
      }
    } else {
      toaster("error", responseData.error);
    }

    setIsChildLoading && setIsChildLoading(false);
  };

  const handleListingBoxes = async (payload: any) => {
    setIsChildLoading && setIsChildLoading(true);

    //TODO: edit the offset filters once it moves to page, change status to the appropriate one as well
    let filters = pick(payload, [
      "customerOrgIDs",
      "status",
      "sortingHubID",
      "pickupType",
      "handoverPointID",
      "orFields",
      "ancestorOrgIDs",
      // "driverID",
    ]);

    filters = payload.customerOrgIDs
      ? omit(filters, ["ancestorOrgIDs"])
      : filters;

    const responseData = await listBoxes(
      omitBy(filters, isNil),
      {
        offset: queryParams.offset,
        limit: toNum(itemPerPage),
      },
      { sortBy: "status", mode: SortingMode.DESC },
    );
    if (responseData && !responseData.error) {
      setCurrentListCount(responseData.length);
      setAllOrderList(responseData);
      setDriverOptions &&
        setDriverOptions(handleSetUpDriverOptions(responseData));
    } else {
      toaster("error", responseData.error);
    }

    setIsChildLoading && setIsChildLoading(false);
  };

  const handleListingTrolley = async (payload: any) => {
    setIsChildLoading && setIsChildLoading(true);

    let filters = pick(payload, [
      "sortingHubID",
      "customerOrgIDs",
      "orFields",
      "status",
      "ancestorOrgIDs",
    ]);
    filters.status = ["LOADING", "LOADED"];
    filters = payload.customerOrgIDs
      ? omit(filters, ["ancestorOrgIDs"])
      : filters;

    const responseData = await listTrolley(
      omitBy(filters, isNil),
      {
        offset: queryParams.offset,
        limit: toNum(itemPerPage),
      },
      { sortBy: "status", mode: SortingMode.DESC },
    );
    if (responseData && !responseData.error) {
      setCurrentListCount(responseData.length);
      setAllOrderList(responseData);
    } else {
      toaster("error", responseData.error);
    }

    setIsChildLoading && setIsChildLoading(false);
  };

  const handleCreateBatchParcel = async (input) => {
    const responseData: any = await createBatch(input);

    if (responseData && !responseData.error) {
      toaster("success", "Batch created successfully");
      handleAPICalls(refreshPayload);
    } else {
      toaster("error", responseData.error.message);
    }
  };

  const handleAddToBatch = async (batchParcelID, parcels) => {
    const responseData = await addToBatch(batchParcelID, parcels);
    if (responseData && !responseData.error) {
      toaster("success", "Added to Batch");
      setTimeout(() => handleAPICalls(refreshPayload), 3000);
    } else {
      toaster("error", responseData.error.message);
    }
  };

  const handleSetHandoverPoint = async (payload: any) => {
    const responseData = payload.orderIDs
      ? await updateOrdersHandoverPoint(payload)
      : await updateBatchHandoverPoint(payload);

    if (responseData && !responseData.error) {
      toaster("success", "Successfully assigned a handover point.");

      // previous dev made this a "refresh function"
      // without delay, the backend will be unable to send the updated copy quickly
      // -> need a better solution
      setTimeout(() => handleAPICalls(refreshPayload), 2000);
    } else {
      toaster("error", responseData.error.message.message);
    }
  };

  const handleAssignBoxDriver = async (input) => {
    const responseData = await boxAssignDriver(input);

    if (!isNil(responseData) && responseData.updated) {
      toaster("success", "Assigned Driver successfully");
      handleAPICalls(refreshPayload);
    } else {
      toaster("error", "Failed to update");
    }
  };

  const handleAssignBulkLooseDriver = async (input) => {
    const responseData: any = await bulkLooseAssignDriver(omitBy(input, isNil));

    if (!isNil(responseData) && responseData.updated) {
      toaster("success", "Assigned Driver successfully");
      handleAPICalls(refreshPayload);
    } else {
      toaster("error", "Failed to update");
    }
  };

  return {
    allOrderList,
    allBatchesList,
    isChildLoading,
    driverOptions,
    handleAPICalls,
    handleCreateBatchParcel,
    handleAddToBatch,
    handleSetHandoverPoint,
    handleAssignBoxDriver,
    handleAssignBulkLooseDriver,
  };
};

const RadioComponent: React.FC<any> = ({
  radioButtonState,
  setRadioButtonState,
  t,
}) => {
  const { isCustomer } = useCorporateType();

  const radioOptions = useMemo(() => {
    if (isCustomer)
      return (
        <>
          <Radio.Button value="0">{t("option.parcels")}</Radio.Button>
        </>
      );

    return (
      <>
        <Radio.Button value="0">{t("option.parcels")}</Radio.Button>
        <Radio.Button value="3">{t("option.batches")}</Radio.Button>
        <Radio.Button value="1">{t("option.bulk")}</Radio.Button>
        <Radio.Button value="2">{t("option.loose")}</Radio.Button>
        <Radio.Button value="4">{t("option.boxes")}</Radio.Button>
        <Radio.Button value="5">{t("option.trolley")}</Radio.Button>
      </>
    );
  }, [isCustomer]);

  return (
    <Radio.Group
      className="m-20 mb-10"
      defaultValue="0"
      value={radioButtonState}
      buttonStyle="solid"
      onChange={(event) => {
        setRadioButtonState(event.target.value);
      }}
    >
      {radioOptions}
    </Radio.Group>
  );
};

const SortingList: React.FC<SortingListProps> = ({
  customerOptions,
  sortingHubOptions,
  vehicleOptions,
  loadingMechanism,
  queryParams,
  itemPerPage,
  setCurrentListCount,
}) => {
  const { t } = useTranslation();
  const query = useQuery();
  const history = useHistory();
  const subTab = query.get("subTab");
  const { org } = useSession();
  const globalFilterState = useRecoilValue(GlobalFilterAtom);
  let initialFilters = {
    orgID: globalFilterState?.selectedOrgIDs?.length ? null : [org._id],
    ancestorOrgIDs: globalFilterState?.selectedOrgIDs?.length
      ? globalFilterState?.selectedOrgIDs
      : null,
  };
  const [radioButtonState, setRadioButtonState] = useState(subTab ?? "0");
  const [refreshPayload, setRefreshPayload] = useState<any>(initialFilters);
  const { isLoading } = loadingMechanism;

  const {
    allOrderList,
    allBatchesList,
    isChildLoading,
    driverOptions,
    handleAPICalls,
    handleCreateBatchParcel,
    handleAddToBatch,
    handleSetHandoverPoint,
    handleAssignBoxDriver,
    handleAssignBulkLooseDriver,
  } = useSortingList({
    radioButtonState,
    queryParams,
    itemPerPage,
    setCurrentListCount,
    refreshPayload,
  });

  const onFilter = (filterInput) => {
    if (isEmpty(filterInput)) {
      handleAPICalls(initialFilters);
      return;
    }

    const filterValue = {
      orgID: filterInput.customersList
        ? [filterInput.customersList]
        : [org._id],
      customerOrgIDs: filterInput.customerOrgList
        ? [filterInput.customerOrgList]
        : null,
      vehicleType: filterInput.vehicleType || null,
      driver: filterInput.driver || null,
      orFields: filterInput.orFields || null,
      handoverPointID: filterInput.handoverPoint || null,
      sortingHubID: filterInput.sortingHub || null,
      deliveryType: filterInput.deliveryType || null,
      status: filterInput.status ? [filterInput.status] : null,
      pickupType: filterInput.pickupType || null,
      destination: filterInput.destination || null,
    };

    setRefreshPayload(filterValue);
    handleAPICalls(filterValue);
  };

  const tabs = [
    {
      key: "0",
      component: (
        <SortingParcelsList
          onFilter={onFilter}
          customerOptions={customerOptions}
          sortingHubOptions={sortingHubOptions}
          allOrderList={allOrderList}
          onCreateBatch={handleCreateBatchParcel}
          onAddToBatch={handleAddToBatch}
          allBatchesList={allBatchesList}
          handoverOptions={uniqBy(
            allOrderList
              .map((order) => order.midMileInfoData?.handoverPoint)
              .filter((handoverPoint) => handoverPoint?.id !== null),
            "id",
          )}
        />
      ),
    },
    {
      key: "1",
      component: (
        <SortingBulkList
          onFilter={onFilter}
          customerOptions={customerOptions}
          sortingHubOptions={sortingHubOptions}
          vehicleOptions={vehicleOptions}
          onAssignDriver={handleAssignBulkLooseDriver}
          onSetHandoverPoint={handleSetHandoverPoint}
          allOrderList={allOrderList}
        />
      ),
    },
    {
      key: "2",
      component: (
        <SortingLooseList
          onFilter={onFilter}
          customerOptions={customerOptions}
          sortingHubOptions={sortingHubOptions}
          vehicleOptions={vehicleOptions}
          onAssignDriver={handleAssignBulkLooseDriver}
          onSetHandoverPoint={handleSetHandoverPoint}
          allOrderList={allOrderList}
        />
      ),
    },
    {
      key: "3",
      component: (
        <SortingBatchesList
          onFilter={onFilter}
          customerOptions={customerOptions}
          sortingHubOptions={sortingHubOptions}
          onSetHandoverPoint={handleSetHandoverPoint}
          allOrderList={allOrderList}
        />
      ),
    },
    {
      key: "4",
      component: (
        <SortingBoxesList
          onFilter={onFilter}
          customerOptions={customerOptions}
          sortingHubOptions={sortingHubOptions}
          vehicleOptions={vehicleOptions}
          onAssignDriver={handleAssignBoxDriver}
          allOrderList={allOrderList}
          handoverPointOptions={uniqBy(
            allOrderList
              .map((box) => box.handoverPointData)
              .filter((handoverPoint) => handoverPoint?.id !== null),
            "id",
          )}
          driverOptions={driverOptions}
        />
      ),
    },
    {
      key: "5",
      component: (
        <SortingTrolleyList
          onFilter={onFilter}
          customerOptions={customerOptions}
          sortingHubOptions={sortingHubOptions}
          allOrderList={allOrderList}
        />
      ),
    },
  ];

  useEffect(() => {
    // onFilter(initialFilters);
    history.push(
      `/delivery-management/parcel-tracking?tabKey=2&subTab=${radioButtonState}`,
    );
    // handleSortingTypeChange()
  }, [radioButtonState]);

  useEffect(() => {
    if (
      !isEmpty(globalFilterState) &&
      globalFilterState?.selectedOrgIDs?.length
    ) {
      onFilter({ ancestorOrgIDs: globalFilterState?.selectedOrgIDs });
    }
  }, [globalFilterState]);

  return (
    <div className="parcel-order-list">
      <div>
        <RadioComponent
          radioButtonState={radioButtonState}
          setRadioButtonState={setRadioButtonState}
          t={t}
        />
        <Tooltip title="Refresh" placement="right">
          <Button
            className="op-refresh-btn"
            icon={isChildLoading ? <LoadingOutlined /> : <ReloadOutlined />}
            isDisabled={isChildLoading || isLoading}
            onClick={() => handleAPICalls(refreshPayload)}
            shape="circle"
          />
        </Tooltip>
      </div>
      {isLoading ? (
        <div className="loading-container">
          <Spin size="large" indicator={<LoadingOutlined />} />
        </div>
      ) : (
        <ErrorBoundary>{tabs[radioButtonState].component}</ErrorBoundary>
      )}
    </div>
  );
};

export default SortingList;
